import React, { useEffect, useRef } from "react";
import { Dialog } from "@mui/material";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   Scoped styles — no MUI overrides needed
───────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=DM+Sans:wght@400;500;600&family=DM+Mono&display=swap');

  .ncm-root {
    font-family: 'DM Sans', sans-serif;
    background: #FAFAF8;
    border-radius: 28px !important;
    overflow: hidden;
    position: relative;
    padding: 0;
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.06),
      0 24px 64px rgba(0,0,0,0.12),
      0 4px 16px rgba(0,0,0,0.06);
  }

  /* Decorative arc at the top */
  .ncm-arc {
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 320px;
    height: 220px;
    background: radial-gradient(ellipse at 50% 40%, #FFF3CD 0%, #FFE28A 40%, transparent 72%);
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;
    filter: blur(2px);
    opacity: 0.9;
  }

  .ncm-inner {
    position: relative;
    z-index: 1;
    padding: 40px 36px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
  }

  /* Coin icon ring */
  .ncm-icon-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FFF8E1 0%, #FFE57F 100%);
    box-shadow:
      0 0 0 8px rgba(255, 214, 0, 0.12),
      0 4px 20px rgba(255, 180, 0, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    animation: ncm-pulse 2.8s ease-in-out infinite;
  }

  @keyframes ncm-pulse {
    0%, 100% { box-shadow: 0 0 0 8px rgba(255,214,0,0.12), 0 4px 20px rgba(255,180,0,0.25); }
    50%       { box-shadow: 0 0 0 14px rgba(255,214,0,0.08), 0 4px 28px rgba(255,180,0,0.35); }
  }

  .ncm-coin-svg {
    width: 36px;
    height: 36px;
  }

  /* Title */
  .ncm-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px;
    font-weight: 600;
    color: #1A1714;
    letter-spacing: -0.3px;
    text-align: center;
    margin: 0 0 8px;
    line-height: 1.2;
  }

  .ncm-subtitle {
    font-size: 14px;
    color: #8A8580;
    text-align: center;
    margin: 0 0 28px;
    line-height: 1.5;
    max-width: 240px;
  }

  /* Stats pill row */
  .ncm-stats {
    display: flex;
    gap: 10px;
    width: 100%;
    margin-bottom: 24px;
  }

  .ncm-stat {
    flex: 1;
    background: #FFFFFF;
    border: 1px solid rgba(0,0,0,0.07);
    border-radius: 16px;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  }

  .ncm-stat-label {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #B0A898;
  }

  .ncm-stat-value {
    font-family: 'DM Sans', sans-serif;
    font-size: 22px;
    font-weight: 600;
    color: #1A1714;
    line-height: 1;
  }

  .ncm-stat-value.deficit {
    color: #E05A2B;
  }

  /* Divider with label */
  .ncm-divider {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;
  }

  .ncm-divider-line {
    flex: 1;
    height: 1px;
    background: rgba(0,0,0,0.07);
  }

  .ncm-divider-text {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.12em;
    color: #C0BAB2;
    text-transform: uppercase;
  }

  /* Info box */
  .ncm-info {
    width: 100%;
    background: linear-gradient(135deg, #FFFBF0 0%, #FFF8E7 100%);
    border: 1px solid rgba(255, 180, 0, 0.2);
    border-radius: 14px;
    padding: 14px 16px;
    margin-bottom: 28px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .ncm-info-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #F5A623;
    margin-top: 5px;
    flex-shrink: 0;
  }

  .ncm-info-text {
    font-size: 13px;
    color: #6B5F45;
    line-height: 1.55;
  }

  .ncm-info-text strong {
    color: #3D2F0E;
    font-weight: 600;
  }

  /* Buttons */
  .ncm-actions {
    display: flex;
    gap: 10px;
    width: 100%;
  }

  .ncm-btn {
    flex: 1;
    height: 48px;
    border-radius: 14px;
    border: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
    letter-spacing: 0.01em;
  }

  .ncm-btn-ghost {
    background: #FFFFFF;
    color: #8A8580;
    border: 1px solid rgba(0,0,0,0.09);
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .ncm-btn-ghost:hover {
    background: #F5F3EF;
    color: #5A5550;
    border-color: rgba(0,0,0,0.14);
  }

  .ncm-btn-primary {
    background: linear-gradient(135deg, #FFD000 0%, #FFA800 100%);
    color: #3D2800;
    box-shadow: 0 4px 16px rgba(255, 168, 0, 0.4), 0 1px 3px rgba(0,0,0,0.08);
    position: relative;
    overflow: hidden;
  }

  .ncm-btn-primary::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.25) 0%, transparent 60%);
    pointer-events: none;
  }

  .ncm-btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(255, 168, 0, 0.45), 0 2px 6px rgba(0,0,0,0.1);
  }

  .ncm-btn-primary:active {
    transform: translateY(0);
  }

  .ncm-btn-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  /* Modal enter animation */
  @keyframes ncm-enter {
    from { opacity: 0; transform: scale(0.94) translateY(12px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  .ncm-root {
    animation: ncm-enter 0.38s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
`;

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const NotEnoughCoinsModal = ({ open, onClose, totalVideoCost, user }) => {
  const navigate = useNavigate();
  const styleRef = useRef(null);

  // Inject scoped styles once
  useEffect(() => {
    if (!styleRef.current) {
      const el = document.createElement("style");
      el.textContent = css;
      document.head.appendChild(el);
      styleRef.current = el;
    }
    return () => {
      if (styleRef.current) {
        styleRef.current.remove();
        styleRef.current = null;
      }
    };
  }, []);

  const handleBuyCoins = () => {
    onClose();
    navigate("/golds");
  };

  const balance = user?.coin_num ?? 0;
  const cost = totalVideoCost ?? 2;
  const deficit = Math.max(0, cost - balance);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: "ncm-root", elevation: 0 }}
    >
      <div className="ncm-inner">
        {/* Warm glow arc */}
        <div className="ncm-arc" />

        {/* Coin icon */}
        <div className="ncm-icon-ring">
          <svg className="ncm-coin-svg" viewBox="0 0 36 36" fill="none">
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="url(#cg)"
              stroke="#E8A800"
              strokeWidth="1.5"
            />
            <circle
              cx="18"
              cy="18"
              r="11"
              fill="none"
              stroke="rgba(255,255,255,0.6)"
              strokeWidth="1"
            />
            <text
              x="18"
              y="23"
              textAnchor="middle"
              fontFamily="'DM Sans', sans-serif"
              fontSize="13"
              fontWeight="700"
              fill="#7A5000"
            >
              ¢
            </text>
            <defs>
              <linearGradient
                id="cg"
                x1="8"
                y1="6"
                x2="28"
                y2="30"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#FFE566" />
                <stop offset="100%" stopColor="#FFAC00" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <h2 className="ncm-title">Insufficient Coins</h2>
        <p className="ncm-subtitle">
          You need more coins to unlock this video.
        </p>

        {/* Balance / Cost / Deficit stats */}
        <div className="ncm-stats">
          <div className="ncm-stat">
            <span className="ncm-stat-label">Balance</span>
            <span className="ncm-stat-value">{balance}</span>
          </div>
          <div className="ncm-stat">
            <span className="ncm-stat-label">Cost</span>
            <span className="ncm-stat-value">2</span>
          </div>
          <div className="ncm-stat">
            <span className="ncm-stat-label">Short by</span>
            <span className="ncm-stat-value deficit">{deficit}</span>
          </div>
        </div>

        {/* Optional total unlock cost */}
        {totalVideoCost !== null && totalVideoCost !== undefined && (
          <>
            <div className="ncm-divider">
              <div className="ncm-divider-line" />
              <span className="ncm-divider-text">Full access</span>
              <div className="ncm-divider-line" />
            </div>

            <div className="ncm-info">
              <div className="ncm-info-dot" />
              <p className="ncm-info-text">
                Unlock all remaining videos in this subject for{" "}
                <strong>{totalVideoCost} coins</strong> total.
              </p>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="ncm-actions">
          <button className="ncm-btn ncm-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="ncm-btn ncm-btn-primary" onClick={handleBuyCoins}>
            <span className="ncm-btn-inner">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <circle cx="7.5" cy="7.5" r="6.5" fill="rgba(0,0,0,0.15)" />
                <circle
                  cx="7.5"
                  cy="7.5"
                  r="4.5"
                  fill="none"
                  stroke="rgba(255,255,255,0.7)"
                  strokeWidth="0.8"
                />
                <text
                  x="7.5"
                  y="11"
                  textAnchor="middle"
                  fontSize="7"
                  fontWeight="700"
                  fill="rgba(0,0,0,0.5)"
                  fontFamily="sans-serif"
                >
                  ¢
                </text>
              </svg>
              Buy Coins
            </span>
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default NotEnoughCoinsModal;
